import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import slugify from '../utils/slugify';
import TableOfContents from '../components/TableOfContents/index.js';
import { useSite } from '../context/SiteContext';

const getMarkdownContent = (siteName) => {
  return  (`
# ${siteName} B2B Merchant API Integration Guide

> **Version:** 1.0.0
> **Last Updated:** January 2026

Welcome to the ${siteName} B2B Merchant API! This guide will help you integrate our **Virtual Account** and **Payout** services into your application.

---

## Getting Started

### Base URL

| Environment | Base URL |
|-------------|----------|
| Staging (Sandbox) | \`https://servicesv3.staging.wayabank.ng/wayabank-b2b-product-middleware\` |
| Production | \`https://services.wayabank.ng/wayabank-b2b-product-middleware\` |

### Prerequisites

Before you begin integration, ensure you have:

1. ✅ A registered ${siteName} merchant account
2. ✅ API credentials (Client ID and Secret Key)
3. ✅ Your IP addresses whitelisted
4. ✅ Subscribed to the Virtual Account and/or Payout products

---

## Authentication

All API requests require a **Bearer Token** in the \`Authorization\` header. This section explains how to obtain and use your access token.

### Generate Access Token

Authenticate your merchant account to get an access token for API calls.

**Endpoint:** \`POST /api/v1/auth/merchant/token\`

**Request Body:**

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| \`clientId\` | string | ✅ | Your unique client ID (UUID format) |
| \`apiKey\` | string | ✅ | Your API key (test or live) |

---

## API Response Format

All API responses follow a consistent structure:

### Success Response

\`\`\`json
{
  "status": "success",
  "message": "Operation successful",
  "data": { ... },
  "timestamp": "2026-01-22 10:30:45"
}
\`\`\`

---

## Virtual Account API

The Virtual Account API allows you to create and manage virtual bank accounts for receiving payments.

### Account Types

| Type | Description | Use Case |
|------|-------------|----------|
| **Static** | Permanent virtual account | Long-term customer accounts, recurring payments |
| **Dynamic** | Temporary virtual account with expiry | One-time payments, invoices, orders |

---

## Payout API

The Payout API enables you to transfer funds from your ${siteName} account to other accounts (within ${siteName} or to external banks).

---

## Webhooks

Webhooks allow you to receive real-time notifications for transaction events.

---

## Error Handling

Standard HTTP status codes are used to indicate the success or failure of an API request.

---

## Best Practices

1. **Security**: Never expose your API keys.
2. **Idempotency**: Use unique references for every transaction.
3. **Monitoring**: Log all API responses and errors.
`);
};

export default function MerchantDocumentation() {
  const { settings, loading } = useSite();
  const siteName = settings.site_name || 'WayaBank';
  const markdownContent = settings.b2b_documentation || getMarkdownContent(siteName);
  const [activeSegment, setActiveSegment] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('h2, h3');
      let current = '';
      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 150) {
          current = section.id;
        }
      });
      setActiveSegment(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const components = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={dracula}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    h1: ({ children }) => {
        const id = slugify(String(children));
        return <h1 id={id} className="waya-doc-h1">{children}</h1>;
    },
    h2: ({ children }) => {
      const id = slugify(String(children));
      return (
        <h2 id={id} className="waya-doc-h2">
          {children}
        </h2>
      );
    },
    h3: ({ children }) => {
      const id = slugify(String(children));
      return (
        <h3 id={id} className="waya-doc-h3">
          {children}
        </h3>
      );
    },
    table: ({ children }) => (
        <div className="table-responsive my-4">
            <table className="table table-bordered align-middle">
                {children}
            </table>
        </div>
    ),
    blockquote: ({ children }) => (
        <div className="waya-doc-blockquote p-3 mb-4 rounded border-start border-4 border-primary bg-light">
            {children}
        </div>
    )
  };

  return (
    <div className="waya-docs-page">
      {/* Spacer to fix navbar overlap */}
      <div style={{ height: '100px' }}></div>
      <div className="container-fluid">
        <div className="row">
          {/* Table of Contents (Desktop Sidebar) */}
          <nav className="col-md-3 col-lg-2 d-none d-md-block waya-docs-sidebar p-4 border-end">
            <div className="sticky-top" style={{ top: '120px' }}>
              <h5 className="mb-4 fw-bold">Merchant Guide</h5>
              <TableOfContents
                activeId={activeSegment}
                content={markdownContent}
              />
            </div>
          </nav>

          {/* Mobile Table of Contents Toggle */}
          <div className="d-md-none p-3 border-bottom sticky-top bg-white">
            <button
              className="btn btn-outline-primary d-flex align-items-center gap-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span>{isMobileMenuOpen ? '✕' : '☰'} Table of Contents</span>
            </button>

            {isMobileMenuOpen && (
              <div className="mt-3 p-3 bg-light rounded border waya-mobile-toc">
                <TableOfContents
                  activeId={activeSegment}
                  content={markdownContent}
                  onItemClick={() => setIsMobileMenuOpen(false)}
                />
              </div>
            )}
          </div>

          {/* Main Content Area */}
          <main className="col-md-9 col-lg-8 py-5 px-4 px-lg-5">
            <ReactMarkdown components={components}>
              {markdownContent}
            </ReactMarkdown>

            {/* Assistance Card */}
            <div className="waya-docs-assistance mt-5 p-4 rounded-4 border bg-light d-flex align-items-center justify-content-between">
              <div>
                <h5 className="fw-bold mb-1">Need specialized help?</h5>
                <p className="mb-0 text-muted">
                  Our integration engineers are here to support your team.
                </p>
              </div>
              <button
                className="waya-btn-primary"
                onClick={() => (window.location.href = '/contact')}
              >
                Contact Developer Support
              </button>
            </div>
          </main>
        </div>
      </div>

    </div>
  );
}
